import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, allArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (article?.sections && article.sections.length > 0) {
      setActiveSection(article.sections[0].id);
    }
  }, [article]);

  if (!article) {
    return <NotFound />;
  }

  const hasFullContent = article.sections && article.sections.length > 0;

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="py-12 md:py-16 bg-background-50">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.title,
                "description": article.metaDescription,
                "author": {
                  "@type": "Organization",
                  "name": article.author,
                },
                "datePublished": article.date,
                "dateModified": "2026-07-21",
                "image": article.image,
                "publisher": {
                  "@type": "Organization",
                  "name": "KakobuyPolska.skin",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
                  },
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://kakobuypolska.skin/artykul/${article.slug}`,
                },
              }),
            }}
          />
          {article.faqItems && article.faqItems.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": article.faqItems.map((item) => ({
                    "@type": "Question",
                    "name": item.question,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": item.answer,
                    },
                  })),
                }),
              }}
            />
          )}
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors duration-200 mb-8 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </div>
              Powrót do strony głównej
            </a>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime} czytania</span>
                <span className="text-xs text-foreground-500">{article.date}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-foreground-600 leading-relaxed max-w-3xl">
                {article.metaDescription}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              {hasFullContent ? (
                <>
                  <aside className="lg:w-64 flex-shrink-0">
                    <div className="lg:sticky lg:top-24">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">Spis treści</h4>
                      <nav className="space-y-1">
                        {article.sections!.map((section) => (
                          <a
                            key={section.id}
                            href={`#${section.id}`}
                            onClick={() => setActiveSection(section.id)}
                            className={`block text-sm py-2 px-3 rounded-lg transition-colors duration-150 cursor-pointer ${
                              activeSection === section.id
                                ? 'bg-primary-100/70 text-primary-700 font-medium'
                                : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                            }`}
                          >
                            {section.title}
                          </a>
                        ))}
                      </nav>

                      <div className="mt-8 p-4 bg-background-100 rounded-xl border border-background-200/60">
                        <p className="text-xs font-semibold text-foreground-800 mb-3">
                          Przejdź do Kakobuy Spreadsheet
                        </p>
                        <a
                          href="https://kaspreadsheet.com"
                          target="_blank"
                          rel="noopener nofollow"
                          className="block w-full text-center px-4 py-2.5 text-xs font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer mb-2"
                        >
                          Kakobuy Spreadsheet
                        </a>
                        <a
                          href="https://kaspreadsheet.com"
                          target="_blank"
                          rel="noopener nofollow"
                          className="block w-full text-center px-4 py-2.5 text-xs font-semibold text-foreground-700 border border-background-300 hover:bg-background-200/50 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          Kup Teraz
                        </a>
                      </div>
                    </div>
                  </aside>

                  <div className="flex-1 min-w-0">
                    <div className="prose-custom space-y-12">
                      {article.sections!.map((section) => (
                        <section key={section.id} id={section.id}>
                          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                            {section.title}
                          </h2>
                          <div
                            className="text-foreground-700 leading-relaxed space-y-4 text-[15px]"
                            dangerouslySetInnerHTML={{ __html: section.content }}
                          />
                        </section>
                      ))}
                    </div>

                    {article.faqItems && article.faqItems.length > 0 && (
                      <div className="mt-16 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/60">
                        <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                          Najczęściej Zadawane Pytania
                        </h3>
                        <div className="space-y-4">
                          {article.faqItems.map((item, idx) => (
                            <div key={idx}>
                              <h4 className="text-sm font-semibold text-foreground-900 mb-1">
                                {item.question}
                              </h4>
                              <p className="text-sm text-foreground-600 leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-12 pt-8 border-t border-background-200/70">
                      <div className="flex flex-wrap items-center gap-3 mb-6">
                        <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          Powiązane kategorie:
                        </span>
                        {categories.slice(0, 6).map((cat) => (
                          <a
                            key={cat.id}
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 border border-background-200/60 hover:border-primary-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
                          >
                            {cat.namePl}
                          </a>
                        ))}
                      </div>

                      <div className="mb-6">
                        <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Więcej artykułów:</span>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {allArticles.filter(a => a.slug !== article.slug).map((a) => (
                            <a
                              key={a.slug}
                              href={`/artykul/${a.slug}`}
                              className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200 border border-secondary-200 hover:border-secondary-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
                            >
                              {a.title.length > 40 ? a.title.slice(0, 40) + '...' : a.title}
                            </a>
                          ))}
                        </div>
                      </div>

                      <a
                        href="/"
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-left-line text-sm"></i>
                        </div>
                        Powrót do strony głównej KakobuyPolska.skin
                      </a>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 min-w-0">
                  <div className="mb-10 rounded-2xl overflow-hidden h-[300px] md:h-[400px]">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-10">
                    <p className="text-foreground-700 leading-relaxed text-[15px]">
                      {article.metaDescription}
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-4">
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Kakobuy Spreadsheet
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-external-link-line text-sm"></i>
                        </div>
                      </a>
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-700 border border-background-300 hover:bg-background-200/50 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Kup Teraz
                      </a>
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-background-200/70">
                    <div className="mb-6">
                      <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Więcej artykułów:</span>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {allArticles.filter(a => a.slug !== article.slug).map((a) => (
                          <a
                            key={a.slug}
                            href={`/artykul/${a.slug}`}
                            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200 border border-secondary-200 hover:border-secondary-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
                          >
                            {a.title.length > 40 ? a.title.slice(0, 40) + '...' : a.title}
                          </a>
                        ))}
                      </div>
                    </div>
                    <a
                      href="/"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-line text-sm"></i>
                      </div>
                      Powrót do strony głównej KakobuyPolska.skin
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}